// src/components/resources/tools/generators/CommentThread/XComments.tsx

'use client'

import type { FC } from 'react'
import { BarChart2, Heart, MessageCircle, Repeat2 } from 'lucide-react'
import { clsx } from 'clsx'
import {
  displayedLikes,
  formatCompactCount,
  formatHandle,
  normalizeHandle,
  type ThreadComment,
} from './threadModel'
import { CommentAvatar, likeButtonLabel, VerifiedCheck, type CommentPreviewProps } from './previewShared'

/**
 * X (Twitter) reply thread, styled to the current UI: each reply renders as a
 * compact post row — display name + verified check + `@handle · time` byline,
 * body text, and the reply/repost/like/views action row — divided by hairlines.
 * Nested replies connect to their parent with X's vertical thread rail.
 * X shows no author or pinned affordance on replies, so those flags are
 * deliberately not rendered here (the editor hides the toggles too). The like
 * heart is a real toggle (aria-pressed): clicking fills X's like pink and
 * bumps the displayed count.
 */
export const XComments: FC<CommentPreviewProps> = ({ comments, theme, onToggleLike }) => {
  const isDark = theme === 'dark'
  const text = isDark ? 'text-[#e7e9ea]' : 'text-[#0f1419]'
  const muted = isDark ? 'text-[#71767b]' : 'text-[#536471]'
  const border = isDark ? 'border-[#2f3336]' : 'border-gray-200'
  const rail = isDark ? 'bg-[#333639]' : 'bg-gray-300'

  const renderRow = (comment: ThreadComment, hasThreadRail = false): JSX.Element => (
    <div className="flex gap-3 px-4 py-3">
      <div className="relative flex flex-col items-center">
        <CommentAvatar username={comment.username} flavor="x" />
        {hasThreadRail && <div aria-hidden="true" className={clsx('mt-1 w-0.5 flex-1 rounded-full', rail)} />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-1 text-[14px]">
          <span className={clsx('inline-flex items-center gap-1 font-bold', text)}>
            {normalizeHandle(comment.username)}
            {comment.verified && <VerifiedCheck className="text-[#1d9bf0]" />}
          </span>
          <span className={muted}>
            {formatHandle(comment.username)} · {comment.timestamp}
          </span>
        </p>
        <p className={clsx('mt-0.5 whitespace-pre-wrap text-[14px] leading-snug', text)}>{comment.text}</p>
        <p className={clsx('mt-2 flex max-w-[260px] items-center justify-between text-[12px]', muted)}>
          <span className="flex items-center gap-1">
            <MessageCircle aria-hidden="true" className="h-3.5 w-3.5" />
            {comment.replies.length > 0 && formatCompactCount(comment.replies.length)}
          </span>
          <Repeat2 aria-hidden="true" className="h-4 w-4" />
          <button
            type="button"
            onClick={() => onToggleLike?.(comment.id)}
            aria-pressed={comment.liked}
            aria-label={likeButtonLabel(comment.liked, comment.username)}
            className={clsx(
              'flex items-center gap-1 rounded p-0.5 transition-transform hover:scale-110',
              comment.liked && 'text-[#f91880]',
            )}
          >
            <Heart className={clsx('h-3.5 w-3.5', comment.liked && 'fill-[#f91880] text-[#f91880]')} />
            {displayedLikes(comment) > 0 && <span>{formatCompactCount(displayedLikes(comment))}</span>}
          </button>
          <BarChart2 aria-hidden="true" className="h-3.5 w-3.5" />
        </p>
      </div>
    </div>
  )

  return (
    <div className={clsx('overflow-hidden rounded-xl border shadow-xl', border, isDark ? 'bg-black' : 'bg-white')}>
      <div className={clsx('divide-y', isDark ? 'divide-[#2f3336]' : 'divide-gray-200')}>
        {comments.map(comment => (
          <div key={comment.id}>
            {renderRow(comment, comment.replies.length > 0)}
            {comment.replies.map(reply => (
              <div key={reply.id} className="pl-6">
                {renderRow(reply)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
